import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Tabs, Tab, Typography, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Checkbox, Button, Alert, Snackbar, CircularProgress, TextField
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { differenceInDays } from 'date-fns';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const MESES_NOMES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

const today = new Date();

const initialNovo = {
  funcionario_id: '',
  tipo_id: '',
  data_inicio: today,
  data_fim: today,
  observacao: '',
};

function PontoPeriodo() {
  const [tabIndex, setTabIndex] = useState(0);
  const { authenticatedFetch } = useAuth();

  const [funcionarios, setFuncionarios] = useState([]);
  const [tipos, setTipos] = useState([]);

  // Filtros de busca
  const [filtroFunc, setFiltroFunc] = useState('');
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroAno, setFiltroAno] = useState('');
  const [filtroAplicado, setFiltroAplicado] = useState('todos');

  // Opções dinâmicas vindas do banco
  const [anosOptions, setAnosOptions] = useState([]);
  const [mesesOptions, setMesesOptions] = useState([]);
  const [isReady, setIsReady] = useState(false);

  const [periodos, setPeriodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const [novo, setNovo] = useState(initialNovo);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // 1. CARREGAMENTO INICIAL: Busca Funcionários, Tipos e os Anos que possuem registros
  const fetchDataInicial = useCallback(async () => {
    try {
      // Busca funcionários para o Select
      const resFunc = await authenticatedFetch(`${API_BASE_URL}/api/ponto-adiantamento-buscar-funcionarios/`);
      if (resFunc.ok) setFuncionarios(await resFunc.json());

      // Busca tipos de falta para o Select
      const resTipos = await authenticatedFetch(`${API_BASE_URL}/api/ponto-falta-editar-faltas-buscar/`);
      if (resTipos.ok) {
        const data = await resTipos.json();
        setTipos(data.map(f => ({ id: f.falta_id, nome: f.falta_nome })));
      }

      // Busca os ANOS que possuem períodos no banco
      const resAnos = await authenticatedFetch(`${API_BASE_URL}/api/ponto-periodo-ano/`);
      if (resAnos.ok) {
        const anos = await resAnos.json();
        setAnosOptions(anos);
      }
      
      setIsReady(true);
    } catch (err) {
      console.error("Erro no carregamento inicial:", err);
    }
  }, [authenticatedFetch]);

  // 2. BUSCAR MESES: Chamado quando o usuário seleciona um Ano
  const handleAnoChange = async (anoSelecionado) => {
    setFiltroAno(anoSelecionado);
    setFiltroMes(''); // Limpa o mês ao trocar de ano
    
    if (!anoSelecionado) {
      setMesesOptions([]);
      return;
    }

    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/ponto-periodo-mes/?ano=${anoSelecionado}`);
      if (res.ok) {
        const meses = await res.json();
        setMesesOptions(meses); // Array simples [1, 2, 5...]
      }
    } catch (err) {
      console.error("Erro ao buscar meses:", err);
    }
  };

  // 3. BUSCAR PERÍODOS: Filtra a tabela principal
  const fetchPeriodos = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroFunc) params.append('funcionario_id', filtroFunc);
      if (filtroMes) params.append('mes', filtroMes);
      if (filtroAno) params.append('ano', filtroAno);
      if (filtroAplicado !== 'todos') params.append('aplicado', filtroAplicado === 'sim' ? '1' : '0');

      const res = await authenticatedFetch(`${API_BASE_URL}/api/ponto-periodo-buscar/?${params}`);
      if (res.ok) setPeriodos(await res.json());
    } catch (err) {
      console.error("Erro ao buscar períodos:", err);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, filtroFunc, filtroMes, filtroAno, filtroAplicado]);

  useEffect(() => {
    fetchDataInicial();
  }, [fetchDataInicial]);

  useEffect(() => {
    if (isReady) fetchPeriodos();
  }, [filtroFunc, filtroMes, filtroAno, filtroAplicado, isReady, fetchPeriodos]);

  const handleAdicionarPeriodo = async () => {
    if (!novo.funcionario_id || !novo.tipo_id) {
      setSnackbar({ open: true, message: 'Selecione funcionário e tipo.', severity: 'warning' });
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        funcionario_id: novo.funcionario_id,
        tipo_id: novo.tipo_id,
        data_inicio: novo.data_inicio.toISOString().split('T')[0],
        data_fim: novo.data_fim.toISOString().split('T')[0],
        observacao: novo.observacao || '',
      };

      const res = await authenticatedFetch(`${API_BASE_URL}/api/ponto-periodo-adicionar/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const json = await res.json();
        setSnackbar({ open: true, message: json.erro || 'Erro ao salvar.', severity: 'error' });
        return;
      }

      setSnackbar({ open: true, message: `Período registrado com sucesso!`, severity: 'success' });
      setNovo(initialNovo);
      fetchDataInicial(); // Recarrega anos caso tenha cadastrado um ano novo
      setTabIndex(0);
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao salvar.', severity: 'error' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Box sx={{ p: 1 }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2 }}>
          <Tab label="Listagem de Períodos" />
          <Tab label="Novo Período" />
        </Tabs>

        <TabPanel value={tabIndex} index={0}>
          {!isReady ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Sincronizando filtros...</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              
              {/* SELECT FUNCIONÁRIO */}
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>Funcionário</InputLabel>
                <Select value={filtroFunc} label="Funcionário" onChange={e => setFiltroFunc(e.target.value)}>
                  <MenuItem value=""><em>Todos</em></MenuItem>
                  {funcionarios.map(f => (
                    <MenuItem key={f.id} value={f.id}>{f.nome}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* SELECT ANO - Dinâmico */}
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Ano</InputLabel>
                <Select
                  value={filtroAno}
                  label="Ano"
                  onChange={e => handleAnoChange(e.target.value)}
                >
                  <MenuItem value=""><em>Nenhum</em></MenuItem>
                  {anosOptions.map(a => (
                    <MenuItem key={a} value={a}>{a}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* SELECT MÊS - Bloqueado se não tiver Ano */}
              <FormControl sx={{ minWidth: 150 }} size="small" disabled={!filtroAno}>
                <InputLabel>Mês</InputLabel>
                <Select
                  value={filtroMes}
                  label="Mês"
                  onChange={e => setFiltroMes(e.target.value)}
                >
                  <MenuItem value=""><em>Todos do ano</em></MenuItem>
                  {mesesOptions.map(m => (
                    <MenuItem key={m} value={m}>
                      {String(m).padStart(2, '0')} — {MESES_NOMES[m - 1]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* SELECT APLICADO */}
              <FormControl sx={{ minWidth: 130 }} size="small">
                <InputLabel>Status</InputLabel>
                <Select value={filtroAplicado} label="Status" onChange={e => setFiltroAplicado(e.target.value)}>
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="sim">Aplicado</MenuItem>
                  <MenuItem value="nao">Pendente</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {loading && <CircularProgress size={24} sx={{ mb: 2, display: 'block' }} />}

          <TableContainer component={Paper} sx={{ maxHeight: 500, width: '100%' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Funcionário</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Início</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Fim</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Observação</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Aplicado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {periodos.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">Nenhum registro encontrado.</TableCell>
                  </TableRow>
                ) : periodos.map(p => (
                  <TableRow key={p.id}>
                    <TableCell>{p.funcionario_nome}</TableCell>
                    <TableCell>{new Date(p.data_inicio + 'T12:00:00').toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell>{new Date(p.data_fim + 'T12:00:00').toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell>{p.tipo_nome}</TableCell>
                    <TableCell>{p.observacao}</TableCell>
                    <TableCell align="center">
                      <Checkbox checked={!!p.aplicado} disabled size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabIndex} index={1}>
           <Typography variant="h6" gutterBottom>Registrar Novo Período</Typography>
           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 500, mt: 2 }}>
             
             <FormControl fullWidth>
                <InputLabel>Funcionário</InputLabel>
                <Select 
                  value={novo.funcionario_id} 
                  label="Funcionário"
                  onChange={e => setNovo({...novo, funcionario_id: e.target.value})}
                >
                  {funcionarios.map(f => <MenuItem key={f.id} value={f.id}>{f.nome}</MenuItem>)}
                </Select>
             </FormControl>

             <FormControl fullWidth>
                <InputLabel>Tipo de Período</InputLabel>
                <Select 
                  value={novo.tipo_id} 
                  label="Tipo de Período"
                  onChange={e => setNovo({...novo, tipo_id: e.target.value})}
                >
                  {tipos.map(t => <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>)}
                </Select>
             </FormControl>

             <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker 
                  label="Data de Início" 
                  value={novo.data_inicio} 
                  onChange={v => setNovo({...novo, data_inicio: v})} 
                  renderInput={(p) => <TextField {...p} fullWidth />}
                />
                <DatePicker 
                  label="Data de Fim" 
                  value={novo.data_fim} 
                  onChange={v => setNovo({...novo, data_fim: v})} 
                  renderInput={(p) => <TextField {...p} fullWidth />}
                />
             </Box>

             <TextField 
                label="Observação" 
                multiline 
                rows={3} 
                value={novo.observacao}
                onChange={e => setNovo({...novo, observacao: e.target.value})}
             />

             <Button 
                variant="contained" 
                size="large"
                onClick={handleAdicionarPeriodo} 
                disabled={salvando}
             >
               {salvando ? <CircularProgress size={24} /> : 'Salvar Período'}
             </Button>
           </Box>
        </TabPanel>
      </Box>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
}

export default PontoPeriodo;